import React, { useEffect, useState, lazy, Suspense } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import HBN from "../../../assets/HBN.svg";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import defaultProfilePic from "@/assets/default-icon.jpg";
import {
  Typography,
  Avatar,
  IconButton,
  styled,
  Tooltip,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
const LogoutModal = lazy(() => import("@/shared/LogoutModal/LogoutModal"));

const PurpleTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .MuiTooltip-tooltip`]: {
    backgroundColor: "#6366f1",
    color: "#fff",
    fontSize: "0.875rem",
    fontWeight: 500,
    padding: "10px",
    borderRadius: "6px",
  },
}));

export function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const isAuthenticated = Boolean(localStorage.getItem("access_token"));
  const user = useSelector((state) => state.auth.auth);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navigate = useNavigate();
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      setIsOpen(false);
    }
  };
  const handleProfileClick = () => {
    navigate(user?.role === "customer" ? "/customer/settings" : "/vendor/settings");
  };

  return (
    <nav
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${isScrolled
        ? "bg-white/95 backdrop-blur-sm shadow-lg"
        : "bg-white/95 backdrop-blur-sm shadow-sm"
        }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center">
            <img src={HBN} alt="HBN Logo" className="h-10 w-auto" />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline space-x-6">
              <button
                onClick={() => scrollToSection("home")}
                className="text-gray-700 hover:text-purple transition-colors duration-200 font-medium"
              >
                Home
              </button>
              <button
                onClick={() => scrollToSection("categories")}
                className="text-gray-700 hover:text-purple transition-colors duration-200 font-medium"
              >
                First Home Buyer Guides
              </button>
              <button
                onClick={() => scrollToSection("categories")}
                className="text-gray-700 hover:text-purple transition-colors duration-200 font-medium"
              >
                Explore Services
              </button>
              {isAuthenticated ? (
                <>
                  <Button
                    onClick={handleProfileClick}
                    className="flex items-center gap-2 border border-gray-300 bg-transparent hover:bg-transparent"
                  >
                    <Avatar
                      src={user?.profile_picture?.file_url || defaultProfilePic}
                      alt="User"
                      sx={{ width: 38, height: 38 }}
                    />
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#333",
                        fontWeight: 500,
                        maxWidth: 140,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {(() => {
                        const name =
                          user?.full_name?.trim() ||
                          `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
                        return name.length > 15 ? `${name.slice(0, 15)}…` : name;
                      })()}
                    </Typography>
                  </Button>

                  <PurpleTooltip title="Logout" arrow>
                    <IconButton onClick={() => setLogoutOpen(true)}>
                      <LogoutIcon />
                    </IconButton>
                  </PurpleTooltip>
                </>
              ) : (
                <>
                  {/* <Button
                    onClick={() => navigate("/vendor/auth/login")}
                    variant="outline"
                    className="text-purple border-purple hover:bg-purple hover:text-white"
                  >
                    Signup as Expert
                  </Button> */}
                  <Button
                    className="bg-purple text-white hover:bg-rgb(106, 0, 244)"
                    onClick={() => navigate("/customer/auth/login")}
                  >
                    Signup / Login
                  </Button>
                </>)}
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-700 hover:text-hbn-blue"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="md:hidden bg-white border-t">
          <div className="px-2 pt-2 pb-3 space-y-1">
            <button
              onClick={() => scrollToSection("home")}
              className="block px-3 py-2 text-gray-700 hover:text-purple w-full text-left"
            >
              Home
            </button>
            <button
              onClick={() => scrollToSection("categories")}
              className="block px-3 py-2 text-gray-700 hover:text-purple w-full text-left"
            >
              First Home Buyer Guides
            </button>
            <button
              onClick={() => scrollToSection("categories")}
              className="block px-3 py-2 text-gray-700 hover:text-purple w-full text-left"
            >
              Explore Services
            </button>

            {isAuthenticated ? (
              <>
                <Button
                  onClick={handleProfileClick}
                  className="flex items-center gap-2 border border-gray-300 bg-transparent hover:bg-transparent"
                >
                  <Avatar
                    src={user?.profile_picture?.file_url || defaultProfilePic}
                    alt="User"
                    sx={{ width: 38, height: 38 }}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#333",
                      fontWeight: 500,
                      maxWidth: 140,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {(() => {
                      const name =
                        user?.full_name?.trim() ||
                        `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
                      return name.length > 15 ? `${name.slice(0, 15)}…` : name;
                    })()}
                  </Typography>
                </Button>

                <PurpleTooltip title="Logout" arrow>
                  <IconButton onClick={() => setLogoutOpen(true)}>
                    <LogoutIcon />
                  </IconButton>
                </PurpleTooltip>
              </>
            ) : (
              <>
                {/* <Button
                  variant="outline"
                  className="block mx-3 my-2 text-purple border-purple hover:bg-purple hover:text-white w-auto"
                  onClick={() => navigate("/vendor/auth/login")}
                >
                  Signup as Expert
                </Button> */}
                <Button
                  className="block mx-3 my-2 bg-purple text-white hover:bg-purple/90 w-auto"
                  onClick={() => navigate("/customer/auth/login")}
                >
                 Signup / Login
                </Button>

                {/* Logout Modal */}
                {logoutOpen && (
                  <Suspense
                    fallback={
                      <Box textAlign="center" p={2}>
                        Loading…
                      </Box>
                    }
                  >
                    <LogoutModal
                      open={logoutOpen}
                      onClose={() => setLogoutOpen(false)}
                      onLogout={handleLogout}
                    />
                  </Suspense>
                )}
              </>)}
          </div>
        </div>
      )}
    </nav>
  );
}
